import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Link,
  Chip,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { AuthContext } from "../contexts/AuthContext";
import currencyList from "../utils/static/currency";
import { useErrorDialog } from "../contexts/ErrorDialogContext";
import CorrespondencesTab from "../components/CompanyPage/CorrespondencesTab";
import TaxComputationTab from "../components/CompanyPage/TaxComputationTab";
import { useSuccessDialog } from "../contexts/SuccessDialogContext";
import ComponentLoading from "../components/App/ComponentLoading";
import { formatDateWithYear } from "../utils/hooks/dataHandler";
import { readIrasOAuthReturnCompany } from "../components/CompanyPage/CorrespondencesTab/irasOAuthHelpers";

const getPersistedIrasCompany = () => readIrasOAuthReturnCompany();

const normalizeCompanyTab = (tab) => {
  const parsed = Number(tab);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed >= 1 ? 1 : 0;
};

const formatRoleLabel = (role) => {
  if (!role) return "Loading";
  return String(role).charAt(0).toUpperCase() + String(role).slice(1);
};

const formatFinancialYearEnd = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : formatDateWithYear(date, "short");
};

const CompanyPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const oauthParams = new URLSearchParams(location.search);
  const isIrasOauthCallback =
    oauthParams.has("code") ||
    oauthParams.has("state") ||
    oauthParams.has("error");
  const company = useMemo(
    () => location.state?.company || getPersistedIrasCompany() || {},
    [location.state?.company]
  );
  const companyId = company?.c_id ?? company?.id ?? null;
  const {
    global_authState,
    checkAccess,
    registerCompanyRole,
    clearCompanyRole,
    getCompanyRole,
    createTaxComputation,
    updateTaxComputation,
    deleteTaxComputation,
    getTaxComputationList,
    getExchangeRate,
  } = useContext(AuthContext);
  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();
  const [companyAccess, setCompanyAccess] = useState({
    c_id: companyId,
    role: null,
    loading: true,
  });

  useEffect(() => {
    let isActive = true;
    const roleFallback = "admin";

    const loadRole = async () => {
      clearCompanyRole();
      setCompanyAccess({ c_id: companyId, role: null, loading: true });

      if (!companyId) {
        setCompanyAccess({ c_id: null, role: roleFallback, loading: false });
        registerCompanyRole("admin", null);
        return;
      }

      try {
        const data = await getCompanyRole({ c_id: companyId });
        const nextRole = data?.role ?? roleFallback;
        if (isActive) {
          setCompanyAccess({
            c_id: companyId,
            role: nextRole,
            loading: false,
          });
          registerCompanyRole(nextRole, companyId);
        }
      } catch {
        if (isActive) {
          setCompanyAccess({
            c_id: companyId,
            role: roleFallback,
            loading: false,
          });
          registerCompanyRole(roleFallback, companyId);
        }
      }
    };

    loadRole();

    return () => {
      isActive = false;
      clearCompanyRole();
    };
  }, [companyId, clearCompanyRole, getCompanyRole, registerCompanyRole]);

  const checkCompanyAccess = useCallback(
    (action, module = "", options = {}) =>
      checkAccess(action, module, {
        ...options,
        companyRole: companyAccess.role,
      }),
    [checkAccess, companyAccess.role]
  );

  const [value, setValue] = useState(() => {
    if (isIrasOauthCallback) {
      return 1;
    }
    return normalizeCompanyTab(location.state?.activeTab) ?? 0;
  });

  useEffect(() => {
    if (isIrasOauthCallback) {
      setValue(1);
      return;
    }

    const requestedTab = normalizeCompanyTab(location.state?.activeTab);
    if (requestedTab !== null) {
      setValue(requestedTab);
    }
  }, [isIrasOauthCallback, location.state?.activeTab]);

  const handleChange = async (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: 1,
          position: "sticky",
          top: 0,
          zIndex: 100,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <Link
            component='button'
            type='button'
            variant='body2'
            underline='hover'
            onClick={() => navigate("/consolePanel")}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: 0.5,
              fontSize: "0.75rem",
              lineHeight: 1.5,
              color: "text.secondary",
              "&:hover": { color: "primary.main" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 14 }} />
            Console Panel
          </Link>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: 1,
                bgcolor: "action.hover",
                color: "primary.main",
              }}
            >
              <BusinessIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                component='h1'
                variant='h6'
                fontWeight={600}
                sx={{
                  fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
                  lineHeight: 1.3,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {company.name || "Company"}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 1.5,
                  rowGap: 0.5,
                  mt: 0.375,
                }}
              >
                <Typography variant='caption' sx={{ overflowWrap: "anywhere" }}>
                  <Box component='span' sx={{ color: "text.secondary", mr: 0.5 }}>
                    UEN
                  </Box>
                  {company.uen || "Not set"}
                </Typography>
                <Typography variant='caption'>
                  <Box component='span' sx={{ color: "text.secondary", mr: 0.5 }}>
                    FYE
                  </Box>
                  {formatFinancialYearEnd(company.financialYearEnd)}
                </Typography>
                {global_authState?.subscription?.plan_name ===
                  "Tax Professional" && (
                  <Chip
                    label={`Role: ${formatRoleLabel(companyAccess.role)}`}
                    size='small'
                    color='primary'
                    variant='outlined'
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Tabs
          value={value}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
          aria-label='company tabs'
          sx={{
            minHeight: 32,
            px: { xs: 1, sm: 2 },
            "& .MuiTab-root": {
              minHeight: 32,
              fontWeight: "medium",
              fontSize: "0.8125rem",
              textTransform: "none",
              px: 2,
              py: 0.75,
              minWidth: { xs: "auto", sm: 140 },
            },
            "& .Mui-selected": {
              color: "primary.main",
              fontWeight: "bold",
            },
            "& .MuiTabs-indicator": {
              height: 2.5,
            },
          }}
        >
          <Tab label='Tax Computation' />
          <Tab label='Form CS / Form CS (Lite)' />
        </Tabs>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
          bgcolor: "grey.50",
        }}
      >
        {companyAccess.loading ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ComponentLoading />
          </Box>
        ) : value === 0 ? (
          <TaxComputationTab
            company={company}
            currencyList={currencyList}
            checkAccess={checkCompanyAccess}
            getTaxComputationList={getTaxComputationList}
            onUpdateTaxComputation={updateTaxComputation}
            onDeleteTaxComputation={deleteTaxComputation}
            createTaxComputation={createTaxComputation}
            handleSuccess={handleSuccess}
            handleError={handleError}
            getExchangeRate={getExchangeRate}
          />
        ) : (
          <CorrespondencesTab
            company={company}
            getTaxComputationList={getTaxComputationList}
            onBackToTaxComputation={() => setValue(0)}
          />
        )}
      </Box>
    </Box>
  );
};

export default CompanyPage;
